import { config } from "../../../app-confi/app-config";
import { alignment } from "../models/alignment.enum";
import { columnType } from "./models/column-type.enum";
import { headType } from "./models/head-type.enum";
import { BaseBabylonService } from "../base-babylon.service";
import { SceneManager } from "../../../common/scene.manager";
import { BuildingHelperService } from "../../../app-confi/services/building-helper.service";
import { accuracyEnum } from "../../../common/models/accuracy.enum";
import { partTypeEnum } from "./models/part-type.enum";
import { facadeType } from "./models/facade-type.enum";
import { HybridColumn, HybridPart } from "./models/props-config";
import { segmentType } from "./models/segment-type";
import { Point3 } from "../../../common/models/point.type";

enum variantEnum {
    woodFacade,
    tiledFacade,
}

export class HybridBabylonService extends BaseBabylonService {

    private bhs: BuildingHelperService;

    constructor(
        sceneManager: SceneManager,
        buildingHelperService: BuildingHelperService,
    ) {
        super(sceneManager);
        this.bhs = buildingHelperService;

    }

    isMoosPlazaColumn(type: columnType | headType) {
        return (
            type === columnType.V_P3 ||
            type === columnType.V_P4_1 ||
            type === columnType.V_P4_2 ||
            type === columnType.V_P5 ||
            type === columnType.V_P4H ||
            type === columnType.V_P5H_S1S2 ||
            type === columnType.V_P5H_XL
        )
    }

    mapPartsTo3D(
        partsIn: HybridPart[],
        moosPlazaAlignment: alignment,
        doNotOrder?: boolean,
    ): HybridPart[] {
        // make copy, dont manipulate existing columns
        let offsetBasedOnColumn = 0;
        if (partsIn.length > 0) {
            partsIn.forEach(part => {
                let columnsInOrder: HybridColumn[] = part.columns;

                if (!doNotOrder && part.type === partTypeEnum.body) {
                    // only body needs to be ordered as head are in separate parts
                    columnsInOrder = [...this.orderColumns(moosPlazaAlignment, part.columns)];
                }
                // this needs to be updated to only clean meshes for that building
                columnsInOrder.forEach((column) => {
                    const columnSegments = column.segments;
                    column.unitsAndAmenities = [];
                    let currentSegmentHeight = 0;
                    columnSegments.forEach(segment => {
                        if (segment.type === segmentType.plinth) {
                            if (!segment.custom) {
                                const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, 0);
                                column.unitsAndAmenities.push(unitsAndAmenities);
                            }
                        } else if (segment.type === segmentType.middle) {
                            for (let f = 0; f < segment.floorsInSegment; f++) {
                                const height = f * config.hybridFloorHeight;
                                const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, currentSegmentHeight + height);
                                column.unitsAndAmenities.push(unitsAndAmenities);
                            }
                        } else if (segment.type === segmentType.crown) {
                            const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, currentSegmentHeight);
                            column.unitsAndAmenities.push(unitsAndAmenities);
                        }
                        if (!segment.customSegmentHeight) {
                            currentSegmentHeight += segment.floorsInSegment * config.hybridFloorHeight;
                        } else {
                            currentSegmentHeight += segment.customSegmentHeight;
                        }
                    })
                    offsetBasedOnColumn = offsetBasedOnColumn + column.bays * config.hybridBaySize;
                });
                part.columns = columnsInOrder;
            })

            return partsIn;
        }
        return [];
    }

    renderColumnsTo3D(
        buildingId: string | number,
        parts: HybridPart[],
        facade: facadeType,
        locationInSite: { position: Point3, rotation: Point3 },
        accuracy: accuracyEnum,
    ): Promise<any> {
        // make copy, dont manipulate existing columns
        let columns = [...parts.map(p => p.columns).flat().map(c => ({ ...c }))];
        const promises = [];
        if (columns && columns.length > 0) {
            // this needs to be updated to only clean meshes for that building
            let offsetBasedOnColumn = 0;
            columns.forEach(column => {
                const columnPlinthSegment = this.bhs.getColumnSegment(column, segmentType.plinth);
                if (columnPlinthSegment && columnPlinthSegment.custom) {
                    const width = column.bays * config.hybridBaySize;
                    const locationInBuilding: Point3 = [
                        offsetBasedOnColumn + width / 2,
                        columnPlinthSegment.customSegmentHeight / 2,
                        -columnPlinthSegment.customSegmentWidth / 2,
                    ];
                    this.sceneManager.modelLoaderService.createCustomBoxMesh(
                        buildingId,
                        width,
                        columnPlinthSegment.customSegmentWidth,
                        columnPlinthSegment.customSegmentHeight, { groupId: buildingId, subgroupId: column.id, type: columnPlinthSegment.sectionTypes[0], props: {} },
                        locationInBuilding,
                        locationInSite);
                }
                offsetBasedOnColumn = offsetBasedOnColumn + column.bays * config.hybridBaySize;
                const renderPromises = this.renderInstances(buildingId, column.id, column.unitsAndAmenities, locationInSite, null, accuracy, this.determineVariant(facade));
                promises.push(...renderPromises);
            });
        }
        return Promise.all(promises);
    }

    private determineVariant(facade: facadeType): variantEnum {
        let variant;
        switch (facade) {
            case facadeType.wood:
                variant = variantEnum.woodFacade;
                break;
            case facadeType.tiles:
                variant = variantEnum.tiledFacade;
                break;
            default:
                break;
        }

        return variant;
    }

    private orderColumns(moosPlazaAlignment: alignment, columns: HybridColumn[]) {
        const columnsInOrder: HybridColumn[] = [];

        // add left head or moos plein column as first thing in the list
        if (moosPlazaAlignment === alignment.left) {
            const col = columns.find(column => this.isMoosPlazaColumn(column.type))
            if (col) {
                columnsInOrder.push(col);
            }
        }

        // add all in betweens
        const allExceptHeadsStaircasesAndMoosPlein = columns.filter(c => (c.type === columnType.G_L1L2 || c.type === columnType.G_M || c.type === columnType.G_XL || c.type === columnType.G_S1S2));

        const shuffledColumns = this.equalizeColumns(allExceptHeadsStaircasesAndMoosPlein);

        const allCoreColumns = columns.filter(c => c.type === columnType.O_TH || c.type === columnType.O_TLH2);

        const totalMiddleBays = shuffledColumns.map(s => s.bays).reduce((p, c) => p + c, 0);

        const orderWithStaircases = this.bhs.orderStaircasesInBody<HybridColumn>(moosPlazaAlignment, allCoreColumns, totalMiddleBays, shuffledColumns, columns, this.isMoosPlazaColumn);

        columnsInOrder.push(...orderWithStaircases);

        // as a last lets get the right column
        if (moosPlazaAlignment === alignment.right) {
            const col = columns.find(column => this.isMoosPlazaColumn(column.type));
            if (col) {
                columnsInOrder.push(col);
            }
        }

        columnsInOrder.forEach((s, index) => {
            s.order = index;
            s.unitsAndAmenities = [];
        });

        return columnsInOrder.sort(s => s.order);
    }



    private equalizeColumns(spreadColumns: HybridColumn[]): HybridColumn[] {
        const g_mColumns = spreadColumns.filter(c => c.type === columnType.G_M);
        const g_s1s2Columns = spreadColumns.filter(c => c.type === columnType.G_S1S2);
        const g_xlColumns = spreadColumns.filter(c => c.type === columnType.G_XL);
        const g_l1l2Columns = spreadColumns.filter(c => c.type === columnType.G_L1L2);

        const shuffledColumns = [];
        const equalize = () => {
            if (g_mColumns.length > 0) {
                shuffledColumns.push(g_mColumns.pop());
            }
            if (g_s1s2Columns.length > 0) {
                shuffledColumns.push(g_s1s2Columns.pop());
            }
            if (g_xlColumns.length > 0) {
                shuffledColumns.push(g_xlColumns.pop());
            }
            if (g_l1l2Columns.length > 0) {
                shuffledColumns.push(g_l1l2Columns.pop());
            }
            if (shuffledColumns.length !== spreadColumns.length) {
                equalize();
            }
        };
        equalize();
        return shuffledColumns;
    }
}